import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  Input,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { AbstractControl, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms'
import { Router } from '@angular/router'
import { PrivateConfiguration } from '@ftr/contracts/api/configuration'
import { CanHaveUnsavedChanges, UntypedFormComponent } from '@ftr/forms'
import { RemoteData, ToastService } from '@ftr/foundation'
import { AppPaths, CourtSystemPaths } from '@ftr/routing-paths'
import { BehaviorSubject, filter } from 'rxjs'
import { MfaRequirementData, MultifactorAuthenticationService } from '../../services'

type ControlName =
  'mfaEnforcement' | 'mfaEnforcementSsoBypass' | 'mfaEnforcementPublic' | 'mfaEnforcementPublicSsoBypass'

@Component({
  selector: 'ftr-mfa-requirement-form',
  templateUrl: './mfa-requirement-form.component.html',
  styleUrls: ['./mfa-requirement-form.component.css'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class MfaRequirementFormComponent implements OnInit, CanHaveUnsavedChanges {
  @ViewChild(UntypedFormComponent)
  formComponent: UntypedFormComponent<void>

  submission$ = new BehaviorSubject<RemoteData<PrivateConfiguration>>(RemoteData.loading())

  @Input() formData: MfaRequirementData

  readonly mfaEnforcement: ControlName = 'mfaEnforcement'
  readonly mfaEnforcementSsoBypass: ControlName = 'mfaEnforcementSsoBypass'
  readonly mfaEnforcementPublic: ControlName = 'mfaEnforcementPublic'
  readonly mfaEnforcementPublicSsoBypass: ControlName = 'mfaEnforcementPublicSsoBypass'

  formGroup: UntypedFormGroup

  readonly #destroyRef = inject(DestroyRef)

  constructor(
    private readonly formBuilder: UntypedFormBuilder,
    private readonly multifactorAuthenticationService: MultifactorAuthenticationService,
    private readonly toastService: ToastService,
    private readonly router: Router,
  ) {}

  ngOnInit(): void {
    this.setupSubmitNotifier()
    this.setupForm()
  }

  setupForm(): void {
    this.formGroup = this.formBuilder.group({
      [this.mfaEnforcement]: [this.formData.mfaRequirements.mfaEnforcement],
      [this.mfaEnforcementSsoBypass]: [this.formData.mfaRequirements.mfaEnforcementSsoBypass],
      [this.mfaEnforcementPublic]: [this.formData.mfaRequirements.mfaEnforcementPublic],
      [this.mfaEnforcementPublicSsoBypass]: [this.formData.mfaRequirements.mfaEnforcementPublicSsoBypass],
    })

    // Enable/Disable child checkboxes based on parent's current value
    this.toggleChildCheckbox(this.formGroup.controls[this.mfaEnforcement].value, this.mfaEnforcementSsoBypass)
    this.toggleChildCheckbox(
      this.formGroup.controls[this.mfaEnforcementPublic].value,
      this.mfaEnforcementPublicSsoBypass,
    )

    // Enable/Disable child checkboxes based on parent's updated value
    this.formGroup.controls[this.mfaEnforcement].valueChanges.subscribe(checked => {
      this.toggleChildCheckbox(checked, this.mfaEnforcementSsoBypass)
    })
    this.formGroup.controls[this.mfaEnforcementPublic].valueChanges.subscribe(checked => {
      this.toggleChildCheckbox(checked, this.mfaEnforcementPublicSsoBypass)
    })
  }

  submitForm(): void {
    this.multifactorAuthenticationService
      .saveRequirementConfiguration(this.buildSubmission())
      .subscribe(remote => this.submission$.next(remote))
  }

  getAsUnTypedFormControl(name: ControlName): UntypedFormControl {
    return this.getControl(name) as UntypedFormControl
  }

  hasUnsavedChanges(): boolean {
    return this.formComponent && this.formComponent.hasUnsavedChanges()
  }

  private getControl(name: ControlName): AbstractControl {
    return this.formGroup.controls[name]
  }

  private setupSubmitNotifier(): void {
    this.submission$
      .pipe(
        takeUntilDestroyed(this.#destroyRef),
        filter(s => s.isSuccess()),
      )
      .subscribe(async () => {
        await this.router.navigate([AppPaths.CourtSystem, this.formData.courtSystemId, CourtSystemPaths.Admin])
        this.toastService.success('Successfully updated the MFA requirement settings.')
      })
  }

  private buildSubmission(): MfaRequirementData {
    return {
      configurationId: this.formData.configurationId,
      courtSystemId: this.formData.courtSystemId,
      mfaRequirements: {
        mfaEnforcement: this.getControl(this.mfaEnforcement).value,
        mfaEnforcementSsoBypass: this.getControl(this.mfaEnforcementSsoBypass).value,
        mfaEnforcementPublic: this.getControl(this.mfaEnforcementPublic).value,
        mfaEnforcementPublicSsoBypass: this.getControl(this.mfaEnforcementPublicSsoBypass).value,
      },
    }
  }

  private toggleChildCheckbox(isParentChecked: boolean, controlName: ControlName): void {
    if (isParentChecked) {
      this.formGroup.controls[controlName].enable()
    } else {
      this.formGroup.controls[controlName].setValue(false)
      this.formGroup.controls[controlName].disable()
    }
  }
}
